import type { ReactNode } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import CountUp from "./count-up";

export type Tone = "default" | "ok" | "pend" | "warn" | "rej" | "info" | "star";

export const toneText: Record<Tone, string> = {
  default: "text-[#1f2621]", ok: "text-[#2d7a4d]", pend: "text-[#c98a1a]", warn: "text-[#d97a2b]",
  rej: "text-[#c0493b]", info: "text-[#3a6ea5]", star: "text-[#e0a021]",
};
export const toneColor: Record<Tone, string> = {
  default: "#1a4a2e", ok: "#2d7a4d", pend: "#c98a1a", warn: "#d97a2b",
  rej: "#c0493b", info: "#3a6ea5", star: "#e0a021",
};

export function StatModal({
  open, onOpenChange, title, subtitle, icon, children, footer,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] max-h-[88vh] gap-0 overflow-y-auto rounded-2xl border-[#ebe7dc] bg-[#f7f5ef] p-0 sm:max-w-2xl">
        <DialogHeader className="sticky top-0 z-10 border-b border-[#ebe7dc] bg-white px-5 py-4 pr-12 text-left">
          <div className="flex items-center gap-3">
            {icon && (
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#eaf2ec] text-[#2d7a4d] [&_svg]:h-5 [&_svg]:w-5">
                {icon}
              </span>
            )}
            <div>
              <DialogTitle className="text-lg font-bold text-[#1a4a2e]">{title}</DialogTitle>
              <DialogDescription className="text-[13px] text-[#8a9088]">{subtitle ?? " "}</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="space-y-4 px-5 py-4">{children}</div>
        {footer && <div className="sticky bottom-0 border-t border-[#ebe7dc] bg-white px-5 py-3">{footer}</div>}
      </DialogContent>
    </Dialog>
  );
}

export function ModalSection({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-2xl border border-[#ebe7dc] bg-white p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h4 className="text-[13px] font-bold text-[#1a4a2e]">{title}</h4>
        {action}
      </div>
      {children}
    </section>
  );
}

export interface BreakdownItem {
  label: string;
  value: number;
  decimals?: number;
  suffix?: string;
  tone?: Tone;
  hint?: string;
}

export function BreakdownGrid({ items, cols = 3 }: { items: BreakdownItem[]; cols?: 2 | 3 | 4 }) {
  const colCls = cols === 4 ? "sm:grid-cols-4" : cols === 2 ? "sm:grid-cols-2" : "sm:grid-cols-3";
  return (
    <div className={`grid grid-cols-2 gap-2.5 ${colCls}`}>
      {items.map((it) => (
        <div key={it.label} className="rounded-xl border border-[#ebe7dc] bg-white px-3 py-2.5">
          <div className="text-[11.5px] font-medium text-[#8a9088]">{it.label}</div>
          <div className={`text-xl font-bold tracking-tight ${toneText[it.tone ?? "default"]}`}>
            <CountUp value={it.value} decimals={it.decimals} />
            {it.suffix}
          </div>
          {it.hint && <div className="text-[11px] text-[#8a9088]">{it.hint}</div>}
        </div>
      ))}
    </div>
  );
}
